/* Horizontal bar showing goal progress rings with a "+" button and edit support.
   Caches goal names in localStorage to show correctly-sized skeletons while loading. */

import { useState, useEffect } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { GoalProgressRing } from "@/components/goals/GoalProgressRing";
import { CreateGoalDialog } from "@/components/goals/CreateGoalDialog";
import { EditGoalDialog, type EditGoalData } from "@/components/goals/EditGoalDialog";
import type { Id } from "../../../convex/_generated/dataModel";
import { getLocalMidnight } from "@/lib/getLocalMidnight";

const CACHE_KEY = "goalsBar:cachedNames";

function getCachedNames(): string[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) return JSON.parse(raw) as string[];
  } catch { /* ignore */ }
  return [];
}

function setCachedNames(names: string[]) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(names)); } catch { /* ignore */ }
}

export function GoalsBar() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editGoal, setEditGoal] = useState<EditGoalData | null>(null);
  const goalsData = useQuery(api.tagGoals.getAllWithStreaks, { todayTimestamp: getLocalMidnight() });
  const goals = goalsData?.goals;

  const isLoading = goalsData === undefined;
  const hasGoals = goals != null && goals.length > 0;

  useEffect(() => {
    if (goals) {
      setCachedNames(goals.map((g) => g.name));
    }
  }, [goals]);

  const cachedNames = isLoading ? getCachedNames() : [];

  return (
    <>
      <div className="group/goals grid grid-cols-2 gap-1">
        {isLoading && cachedNames.length > 0 ? (
          cachedNames.map((name) => (
            <GoalProgressRingSkeleton key={name} name={name} />
          ))
        ) : hasGoals ? (
          <>
            {goals.map((goal) => (
              <GoalProgressRing
                key={goal.id}
                name={goal.name}
                icon={goal.icon}
                completedMinutes={goal.todayCompletedMinutes}
                goalMinutes={goal.dailyGoalMinutes}
                streak={goal.streak}
                onClick={() =>
                  setEditGoal({
                    id: goal.id as Id<"tagGoals">,
                    name: goal.name,
                    tags: goal.tags,
                    icon: goal.icon,
                    dailyGoalMinutes: goal.dailyGoalMinutes,
                  })
                }
              />
            ))}
            <Button
              variant="ghost"
              size="icon"
              className="size-7 shrink-0 rounded-lg md:opacity-0 md:group-hover/goals:opacity-100 transition-opacity"
              onClick={() => setCreateOpen(true)}
            >
              <Plus className="size-3.5" />
              <span className="sr-only">Add goal</span>
            </Button>
          </>
        ) : isLoading ? null : (
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 rounded-lg text-xs"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="size-3.5" />
            Add Goal
          </Button>
        )}
      </div>

      <CreateGoalDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EditGoalDialog goal={editGoal} onOpenChange={() => setEditGoal(null)} />
    </>
  );
}

/* Skeleton matching GoalProgressRing layout: ring placeholder + name text with shimmer. */
function GoalProgressRingSkeleton({ name }: { name: string }) {
  return (
    <div className="flex items-center gap-1.5 shrink-0 rounded-md px-1.5 py-1">
      <div className="size-7 rounded-full bg-muted animate-pulse shrink-0" />
      <div className="flex flex-col gap-0.5 leading-tight">
        <span className="text-[11px] font-medium text-transparent bg-muted animate-pulse rounded max-w-16 truncate">
          {name}
        </span>
        <span className="text-[10px] text-transparent bg-muted animate-pulse rounded">
          0m/0m
        </span>
      </div>
    </div>
  );
}
